import { Flex, Statistic } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SegmentedOrDropdown, ShrinkingButton, StatCard } from "~/components";
import { ExpiringLeasesResult } from "~/types/finance";
import { COLORS } from "~/utils";
import { formatExpiringWindowTabLabel } from "~/utils/finance/expiringLeases";

export type ExpiringLeasesHomeStatCardProps = {
  expiringLeases: ExpiringLeasesResult;
  verySmallScreen: boolean;
  year: number;
  month: number;
};

export const ExpiringLeasesHomeStatCard = ({
  expiringLeases,
  verySmallScreen,
  year,
  month,
}: ExpiringLeasesHomeStatCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expiringTab, setExpiringTab] = useState(0);

  const orderedBuckets = [
    ...expiringLeases.buckets.filter((bucket) => bucket.days === 0),
    ...expiringLeases.buckets.filter((bucket) => bucket.days !== 0),
  ];
  const orderedTabDays = [
    ...orderedBuckets.map((bucket) => bucket.days),
  ];
  const orderedTabOptions = orderedTabDays.map((days) =>
    formatExpiringWindowTabLabel(days, t),
  );
  const counts = orderedBuckets.map((bucket) => bucket.count);
  const selectedCount = counts[expiringTab] ?? 0;
  const showWarning = selectedCount > 0;

  const handleViewFinance = () => {
    navigate(
      `/finance?tab=operations&year=${year}&month=${month}#expiring-leases`,
    );
  };

  return (
    <StatCard
      title={t("Expiring_leases")}
      description={
        orderedTabDays[expiringTab] === 0
          ? t("Expired")
          : t("Expiring_leases_meaning") + (orderedTabOptions[expiringTab] ?? "")
      }
      style={
        showWarning
          ? {
              borderColor: COLORS.warm_tangerine,
              boxShadow: `0 0 0 1px ${COLORS.warm_tangerine}22`,
            }
          : undefined
      }
      action={
        <SegmentedOrDropdown
          items={orderedTabOptions}
          selected={expiringTab}
          setSelected={setExpiringTab}
          useDropdown={verySmallScreen}
        />
      }
    >
      <Flex style={{ flexWrap: "wrap", alignItems: "flex-end", gap: 8 }}>
        <Statistic
          value={selectedCount}
          valueStyle={
            showWarning
              ? { color: COLORS.warm_tangerine }
              : { color: COLORS.baby_blue }
          }
        />
        <ShrinkingButton
          small={verySmallScreen}
          onClick={handleViewFinance}
          type="primary"
          style={{ marginLeft: "auto" }}
        >
          {t("Details")}
        </ShrinkingButton>
      </Flex>
    </StatCard>
  );
};
